/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useRef, useState } from "react";
import { View } from "react-native";
import moment from "moment";
import Helmet from "react-helmet";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { DateUtils } from "react-day-picker";
import "react-day-picker/lib/style.css";
import dateFnsFormat from "date-fns/format";
import dateFnsParse from "date-fns/parse";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { Text } from "UpduoLibrary";
import { timeResetState } from "config/ResetState";
import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function DayRangePicker() {
  function parseDate(str, format, locale) {
    const parsed = dateFnsParse(str, format, new Date(), { locale });
    if (DateUtils.isDate(parsed)) {
      return parsed;
    }
    return undefined;
  }

  function formatDate(date, format, locale) {
    return dateFnsFormat(date, format, { locale });
  }
  const dispatch = useDispatch();
  const dayRangeStart = useSelector(
    (state: RootState) => state.dayRangeStart || undefined
  );
  const dayRangeEnd = useSelector(
    (state: RootState) => state.dayRangeEnd || undefined
  );

  const [timeout, setStateTimeout] = useState<any>();
  const to = useRef<any>();

  useEffect(() => {
    if (!dayRangeStart && !dayRangeEnd) {
      _handleResetTimeRange();
    }
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const _handleFocusTo = () => {
    // Focus to `to` field. A timeout is required here because the overlays
    // already set timeouts to work well with input fields
    setStateTimeout(setTimeout(() => to.current.getInput().focus(), 0));
  };

  const _handleShowFromMonth = () => {
    if (!dayRangeStart) {
      return;
    }
    if (moment(dayRangeEnd).diff(moment(dayRangeStart), "months") < 2) {
      to.current.getDayPicker().showMonth(dayRangeStart);
    }
  };

  const _handleFromChange = (from: Date) => {
    // Change the from date and focus the "to" input field
    dispatch(ActionCreators.setDayRangeStart(from));
    if (!dayRangeEnd) {
      _handleFocusTo();
    }
    UpduoAnalytics.logEvent("user-dashboard-date-range", {
      type: "start",
      date: String(from),
    });
  };

  const _handleToChange = (to: Date) => {
    dispatch(ActionCreators.setDayRangeEnd(to));
    _handleShowFromMonth();
    UpduoAnalytics.logEvent("user-dashboard-date-range", {
      type: "end",
      date: String(to),
    });
  };

  const _handleResetTimeRange = () => {
    timeResetState();
    dispatch(ActionCreators.setActivityObject({}));
  };

  let fromPlaceholder = "From";
  let toPlaceholder = "To";
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  if (dayRangeStart) {
    fromPlaceholder = new Date(dayRangeStart).toLocaleDateString(
      "en-US",
      options
    );
  }
  if (dayRangeEnd) {
    toPlaceholder = new Date(dayRangeEnd).toLocaleDateString("en-US", options);
  }
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        zIndex: 10,
      }}
    >
      <div className={"InputFromTo"}>
        <DayPickerInput
          placeholder={fromPlaceholder}
          format={"LL"}
          formatDate={formatDate}
          parseDate={parseDate}
          dayPickerProps={{
            selectedDays: [
              dayRangeStart,
              { from: dayRangeStart || undefined, to: dayRangeEnd },
            ],
            disabledDays: { after: dayRangeEnd! },
            toMonth: dayRangeEnd,
            modifiers: {
              start: dayRangeStart || undefined,
              end: dayRangeEnd,
            },
            numberOfMonths: 2,
          }}
          onDayChange={_handleFromChange}
        />
        <View
          style={{
            width: 30,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text>{"—"}</Text>
        </View>
        <span className={"InputFromTo-to"}>
          <DayPickerInput
            // ref={el => (to = el)}
            ref={to}
            placeholder={toPlaceholder}
            format={"LL"}
            formatDate={formatDate}
            parseDate={parseDate}
            dayPickerProps={{
              selectedDays: [
                dayRangeStart,
                {
                  from: dayRangeStart,
                  to: dayRangeEnd,
                },
              ],
              disabledDays: {
                before: dayRangeStart,
                after: new Date(),
              },
              modifiers: {
                start: dayRangeStart,
                end: dayRangeEnd,
              },
              month: dayRangeStart,
              fromMonth: dayRangeStart,
              numberOfMonths: 2,
            }}
            onDayChange={_handleToChange}
          />
        </span>
        <Helmet>
          <style>
            {`
              .DayPickerInput input {
                font-family: ObjectSans-Regular;
                font-size: 16px;
                padding-left: 8px;
                padding-right: 8px;
                padding-top: 6px;
                padding-bottom: 5px;
                border-top-left-radius: 5px;
                border-top-right-radius: 5px;
                border-bottom-left-radius: 5px;
                border-bottom-right-radius: 5px;
                border: 1px solid #AFB7C2;
                width: 150px;
              }
              .InputFromTo {
                font-family: ObjectSans-Regular;
                display: flex;
                flex-direction: row;
              }
              .InputFromTo .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside) {
                background-color: #f0f8ff !important;
                color: #4a90e2;
              }
              .InputFromTo .DayPicker-Day {
                border-radius: 0 !important;
              }
              .InputFromTo .DayPicker-Day--start {
                border-top-left-radius: 50% !important;
                border-bottom-left-radius: 50% !important;
              }
              .InputFromTo .DayPicker-Day--end {
                border-top-right-radius: 50% !important;
                border-bottom-right-radius: 50% !important;
              }
              .InputFromTo .DayPickerInput-Overlay {
                width: 576px;
                margin-top: 15px;
                margin-left: -207px;
              }
              .InputFromTo-to .DayPickerInput-Overlay {
                margin-left: -405px;
              }
            `}
          </style>
        </Helmet>
      </div>
    </View>
  );
}
